import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  CircularProgress,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import ListAltIcon from '@mui/icons-material/ListAlt';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';

// Replace with your API key and endpoints per the official Pi App Platform API documentation
const API_KEY = "wirpwam7vlny8pghkldpp0ptipkxc9fvqdtooo9lqxzs7x5ucvd892l8jo2pccnv";
// For example, the price endpoint might be as follows (check the official docs for the correct URL)
const PRICE_ENDPOINT = "https://api.minepi.com/v2/prices/spot"; 
const ANNOUNCEMENTS_ENDPOINT = "https://api.minepi.com/v2/announcements";

// Define the shortcuts for the card slider
const shortcuts = [
  {
    id: 1,
    title: "Transactions",
    description: "View your transaction history",
    icon: <ListAltIcon fontSize="large" color="primary" />,
    route: "/transactions",
  },
  {
    id: 2,
    title: "QR Payment",
    description: "Display your QR code to receive payments",
    icon: <QrCodeIcon fontSize="large" color="secondary" />,
    route: "/qr",
  },
  {
    id: 3,
    title: "Scan QR",
    description: "Scan a store's QR code to pay",
    icon: <QrCodeScannerIcon fontSize="large" color="action" />,
    route: "/scan",
  },
];

function Home() {
  const [piPrice, setPiPrice] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loadingPrice, setLoadingPrice] = useState(true);
  const [loadingAnn, setLoadingAnn] = useState(true);

  useEffect(() => {
    // Fetch the current Pi price using the official API (refer to the API Reference)
    fetch(PRICE_ENDPOINT, {
      headers: {
        "Authorization": `Key ${API_KEY}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        // Adjust property access as per the API response structure.
        // For example, if the API returns { data: { amount: "..." } }:
        const price = data?.data?.amount || data.price;
        setPiPrice(price);
        setLoadingPrice(false);
      })
      .catch(error => {
        console.error("Error fetching Pi price:", error);
        setPiPrice("N/A");
        setLoadingPrice(false);
      });

    // Fetch announcements (if available)
    fetch(ANNOUNCEMENTS_ENDPOINT, {
      headers: {
        "Authorization": `Key ${API_KEY}`,
      },
    })
      .then(response => response.json())
      .then(data => {
        // Assume API returns an array of announcements under data.announcements.
        setAnnouncements(data?.announcements || []);
        setLoadingAnn(false);
      })
      .catch(error => {
        console.error("Error fetching announcements:", error);
        setAnnouncements([]);
        setLoadingAnn(false);
      });
  }, []);

  return (
    <Box p={2}>
      {/* Header */}
      <Box textAlign="center" mb={4}>
        <Typography variant="h4" gutterBottom>
          Pi QR Payment App
        </Typography>
        <Typography variant="body1" gutterBottom>
          A modern QR payment platform for customers and shops using Pi.
        </Typography>
      </Box>

      {/* Current Price Card */}
      <Box mb={4} textAlign="center">
        <Card sx={{ maxWidth: 400, margin: '0 auto' }} elevation={4}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Price of Pi
            </Typography>
            {loadingPrice ? (
              <CircularProgress size={24} />
            ) : (
              <Typography variant="h4" color="primary">
                {piPrice ? `${piPrice} USD` : "N/A"}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Announcements Section */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          Announcements
        </Typography>
        {loadingAnn ? (
          <Box textAlign="center">
            <CircularProgress size={24} />
          </Box>
        ) : announcements.length === 0 ? (
          <Typography variant="body2" align="center">
            No announcements at the moment.
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {announcements.map((ann, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Card elevation={3}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {ann.title}
                    </Typography>
                    <Typography variant="body2">
                      {ann.message}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Shortcuts Card Slider */}
      <Box mb={4}>
        <Typography variant="h5" gutterBottom>
          Quick Shortcuts
        </Typography>
        <Box
          sx={{
            display: 'flex',
            overflowX: 'auto',
            gap: 2,
            padding: 1,
          }}
        >
          {shortcuts.map((item) => (
            <Card
              key={item.id}
              sx={{ minWidth: 250, flexShrink: 0 }}
              elevation={3}
            >
              <CardActionArea href={item.route}>
                <CardContent>
                  <Box textAlign="center" mb={1}>
                    {item.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom textAlign="center">
                    {item.title}
                  </Typography>
                  <Typography variant="body2" textAlign="center">
                    {item.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

export default Home;
